import ActionPanel from "@/components/layout/actionPanel/ActionPanel";
import AlertMessage from "@/components/layout/message/AlertMessage";
import PaginationControls from "@/components/layout/paginationControls/PaginationControls";
import type { user } from "@/types/api/user.api";
import { Edit, Plus, SlidersHorizontal } from "lucide-react";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useInstitutions } from "../institutionSection/useInstitutions";
import UserFormModal from "../userFormModal/UserFormModal";
import UserTable from "../userTable/UserTable";
import type { userSectionProps } from "./types.props";
import { useUserModal } from "./useUserModal";
import { useUsers } from "./useUsers";

const PAGE_SIZE = 15;

const UserSection: React.FC<userSectionProps> = ({ refreshStats }) => {
    const navigate = useNavigate();

    const {
        data: users,
        count,
        currentPage,
        filters,
        isLoading,
        error: usersError,
        create,
        update,
        setPage,
        setFilters,
    } = useUsers();

    const { data: institutions, setFilters: setInstitutionFilters } = useInstitutions();

    const {
        isModalOpen,
        editingUser,
        apiError,
        handleOpenModal,
        handleCloseModal,
        handleSubmit,
        handleAssignSimulations,
    } = useUserModal({ create, update, refreshStats });

    const userActions = useMemo(
        () => [
            {
                label: "Modificar",
                handler: (user: user) => handleOpenModal(user),
                variant: "secondary" as const,
                icon: <Edit size={16} />,
            },
            {
                label: "Asignar simulacros",
                handler: handleAssignSimulations,
                variant: "primary" as const,
                icon: <SlidersHorizontal size={16} />,
            },
        ],
        [handleOpenModal, navigate],
    );

    const actions = [
        {
            key: "add",
            icon: <Plus size={16} />,
            label: "Añadir usuario",
            variant: "primary" as const,
            onClick: () => handleOpenModal(null),
            tooltip: "Crear un usuario",
        },
    ];

    return (
        <>
            <ActionPanel
                placeholder="Buscar por identificador, nombre, correo..."
                searchTerm={filters.search}
                onSearchChange={(query) => setFilters({ search: query })}
                actions={actions}
            />

            <AlertMessage type="error" title="Error al cargar usuarios">
                {usersError?.generalMessage}
            </AlertMessage>

            <UserTable data={users} isLoading={isLoading} actions={userActions} />

            {count > PAGE_SIZE && (
                <PaginationControls
                    currentPage={currentPage}
                    totalCount={count}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPage}
                />
            )}

            <UserFormModal
                isOpen={isModalOpen}
                isLoading={isLoading}
                initialData={editingUser || undefined}
                institutions={institutions}
                apiError={apiError}
                onClose={handleCloseModal}
                onSubmit={handleSubmit}
                onInstitutionSearchChange={(term) => setInstitutionFilters({ search: term })}
            />
        </>
    );
};

export default UserSection;
